import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Paper,
  Grid,
  Stack,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Skeleton,
  Tooltip,
  Alert,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ListAltIcon from "@mui/icons-material/ListAlt";
import WorkIcon from "@mui/icons-material/Work";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BusinessIcon from "@mui/icons-material/Business";
import Swal from "sweetalert2";
import { api } from "../../utils/api";
import { useAuth } from "../../context/AuthContext";
import CommentsWidget from "../../components/CommentsWidget";

const STATES = [
  "Andaman & Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam",
  "Bihar", "Chandigarh", "Chhattisgarh", "Dadra & Nagar Haveli & Daman & Diu",
  "Delhi", "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jammu & Kashmir",
  "Jharkhand", "Karnataka", "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha",
  "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana",
  "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
];

const GRADES = ["A+", "A", "B", "C", "D"];
const ADDRESS_TYPES = ["Registered Office", "Corporate Office", "Factory", "Warehouse", "Branch"];

const gradeColor = (g) => {
  switch ((g || "").toUpperCase()) {
    case "A+": return "success";
    case "A": return "primary";
    case "B": return "info";
    case "C": return "warning";
    case "D": return "error";
    default: return "default";
  }
};

// ============ REUSABLE ============

function Section({ title, onEdit, actionLabel, onAction, children }) {
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
          {title}
        </Typography>
        {onAction && (
          <Button size="small" startIcon={<AddIcon />} onClick={onAction}>
            {actionLabel || "Add"}
          </Button>
        )}
        {onEdit && (
          <Button size="small" startIcon={<EditIcon />} onClick={onEdit}>
            Edit
          </Button>
        )}
      </Stack>
      <Divider sx={{ mb: 2 }} />
      {children}
    </Paper>
  );
}

function Detail({ label, value }) {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value || "—"}
      </Typography>
    </Grid>
  );
}

// ============ MAIN ============

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can("clients.edit");
  const canDelete = can("clients.delete");

  const [client, setClient] = useState(null);
  const [registration, setRegistration] = useState({});
  const [addresses, setAddresses] = useState([]);
  const [keyPeople, setKeyPeople] = useState([]);
  const [accounting, setAccounting] = useState({});
  const [gstProfile, setGstProfile] = useState({});
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  // dialogs
  const [basicOpen, setBasicOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [accOpen, setAccOpen] = useState(false);
  const [gstOpen, setGstOpen] = useState(false);
  const [addrDialog, setAddrDialog] = useState({ open: false, data: null });
  const [personDialog, setPersonDialog] = useState({ open: false, data: null });
  const [noteOpen, setNoteOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/clients/detail.php?id=${id}`);
      if (res.success) {
        setClient(res.client || {});
        setRegistration(res.registration_details || {});
        setAddresses(res.addresses || []);
        setKeyPeople(res.key_people || []);
        setAccounting(res.accounting_info || {});
        setGstProfile(res.gst_profile || {});
        setNotes(res.notes || []);
      } else {
        setClient(null);
      }
    } catch (e) {
      console.error(e);
      setClient(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const confirmDelete = async (text, cb) => {
    const res = await Swal.fire({
      title: "Delete?",
      text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
    });
    if (res.isConfirmed) {
      try {
        await cb();
        load();
      } catch (e) {
        Swal.fire({ icon: "error", title: "Failed", text: e.message });
      }
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={100} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={200} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={200} />
      </Box>
    );
  }

  if (!client) {
    return (
      <Alert severity="error">Client not found</Alert>
    );
  }

  return (
    <Box>
      {/* HEADER */}
      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "center" }}
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </IconButton>
            <Avatar sx={{ bgcolor: "primary.main", width: 56, height: 56 }}>
              <BusinessIcon />
            </Avatar>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  {client.business_name}
                </Typography>
                {client.grade && (
                  <Chip
                    label={client.grade}
                    size="small"
                    color={gradeColor(client.grade)}
                    sx={{ fontWeight: 700 }}
                  />
                )}
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {client.contact_person} · {client.mobile}
                {client.email && ` · ${client.email}`}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button
              component={RouterLink}
              to={`/clients/${id}/services`}
              startIcon={<ListAltIcon />}
              variant="outlined"
              size="small"
            >
              Services
            </Button>
            <Button
              component={RouterLink}
              to={`/clients/${id}/jobs`}
              startIcon={<WorkIcon />}
              variant="outlined"
              size="small"
            >
              Jobs
            </Button>
            <Button
              component={RouterLink}
              to={`/clients/${id}/invoices`}
              startIcon={<ReceiptIcon />}
              variant="outlined"
              size="small"
            >
              Invoices
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* BASIC INFO */}
      <Section title="Basic Information" onEdit={canEdit ? () => setBasicOpen(true) : null}>
        <Grid container spacing={2}>
          <Detail label="Business Name" value={client.business_name} />
          <Detail label="Contact Person" value={client.contact_person} />
          <Detail label="Email" value={client.email} />
          <Detail label="Mobile" value={client.mobile} />
          <Detail label="WhatsApp" value={client.whatsapp} />
          <Detail label="Business Type" value={client.business_type} />
          <Detail label="Grade" value={client.grade} />
          <Detail
            label="Address"
            value={
              client.address
                ? `${client.address}, ${client.city}, ${client.state} - ${client.pincode}`
                : null
            }
          />
        </Grid>
      </Section>

      {/* REGISTRATION */}
      <Section title="Registration Details" onEdit={canEdit ? () => setRegOpen(true) : null}>
        <Grid container spacing={2}>
          <Detail label="GSTIN" value={client.gstin} />
          <Detail label="GST Reg Date" value={registration.gst_reg_date} />
          <Detail label="GST State" value={registration.gst_state} />
          <Detail label="MCA CIN" value={registration.mca_cin} />
          <Detail label="TAN" value={registration.tan} />
          <Detail label="ITR Type" value={registration.itr_type} />
          <Detail label="IEC Code" value={registration.iec_code} />
        </Grid>
      </Section>

      {/* ADDRESSES */}
      <Section
        title="Addresses"
        actionLabel="Add Address"
        onAction={canEdit ? () => setAddrDialog({ open: true, data: null }) : null}
      >
        {addresses.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No addresses added
          </Typography>
        )}
        <Stack spacing={1.5}>
          {addresses.map((a) => (
            <Paper
              key={a.id}
              variant="outlined"
              sx={{ p: 2, display: "flex", alignItems: "flex-start", gap: 2 }}
            >
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {a.type}
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {a.address}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  {a.city}, {a.state} - {a.pincode}
                </Typography>
              </Box>
              {canEdit && (
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => setAddrDialog({ open: true, data: a })}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      confirmDelete("Delete this address?", () =>
                        api.get(`/client_addresses/delete.php?id=${a.id}`),
                      )
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Paper>
          ))}
        </Stack>
      </Section>

      {/* KEY PEOPLE */}
      <Section
        title="Key People"
        actionLabel="Add Person"
        onAction={canEdit ? () => setPersonDialog({ open: true, data: null }) : null}
      >
        {keyPeople.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No key people added
          </Typography>
        )}
        <Stack spacing={1.5}>
          {keyPeople.map((p) => (
            <Paper
              key={p.id}
              variant="outlined"
              sx={{ p: 2, display: "flex", alignItems: "flex-start", gap: 2 }}
            >
              <Avatar sx={{ bgcolor: "secondary.main" }}>
                {p.name?.[0]?.toUpperCase() || "U"}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {p.name}{" "}
                  <Typography component="span" variant="caption" color="text.secondary">
                    ({p.role})
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {p.email && <>{p.email} · </>}
                  {p.mobile}
                </Typography>
              </Box>
              {canEdit && (
                <IconButton
                  size="small"
                  onClick={() => setPersonDialog({ open: true, data: p })}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {canDelete && (
                <IconButton
                  size="small"
                  color="error"
                  onClick={() =>
                    confirmDelete("Delete this person?", () =>
                      api.get(`/client_key_people/delete.php?id=${p.id}`),
                    )
                  }
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Paper>
          ))}
        </Stack>
      </Section>

      {/* ACCOUNTING */}
      <Section title="Accounting Info" onEdit={canEdit ? () => setAccOpen(true) : null}>
        <Grid container spacing={2}>
          <Detail label="Accounting Cycle" value={accounting.accounting_cycle} />
          <Detail label="Bookkeeper" value={accounting.bookkeeper_name} />
          <Detail label="Platform" value={accounting.accounting_platform} />
          <Detail label="Tally Serial" value={accounting.tally_serial} />
        </Grid>
      </Section>

      {/* GST PROFILE */}
      <Section title="GST Profile" onEdit={canEdit ? () => setGstOpen(true) : null}>
        <Grid container spacing={2}>
          <Detail label="Username" value={gstProfile.gst_username} />
          <Detail
            label="Password"
            value={gstProfile.gst_password ? "••••••••" : null}
          />
          <Detail label="Filing Type" value={gstProfile.gst_filing_type} />
          <Detail
            label="Composition Scheme"
            value={gstProfile.composition_scheme ? "Yes" : "No"}
          />
        </Grid>
      </Section>

      {/* NOTES */}
      <Section
        title="Internal Notes"
        actionLabel="Add Note"
        onAction={canEdit ? () => setNoteOpen(true) : null}
      >
        {notes.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No notes yet
          </Typography>
        )}
        <Stack spacing={1.5}>
          {notes.map((n) => (
            <Paper key={n.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.disabled">
                    {n.created_at}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {n.note}
                  </Typography>
                </Box>
                {canDelete && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      confirmDelete("Delete this note?", () =>
                        api.get(`/client_internal_notes/delete.php?id=${n.id}`),
                      )
                    }
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Section>

      {/* DIALOGS */}
      <BasicInfoDialog
        open={basicOpen}
        onClose={() => setBasicOpen(false)}
        data={client}
        onSaved={() => {
          setBasicOpen(false);
          load();
        }}
      />
      <RegistrationDialog
        open={regOpen}
        onClose={() => setRegOpen(false)}
        data={registration}
        clientId={id}
        onSaved={() => {
          setRegOpen(false);
          load();
        }}
      />
      <AccountingDialog
        open={accOpen}
        onClose={() => setAccOpen(false)}
        data={accounting}
        clientId={id}
        onSaved={() => {
          setAccOpen(false);
          load();
        }}
      />
      <GstDialog
        open={gstOpen}
        onClose={() => setGstOpen(false)}
        data={gstProfile}
        clientId={id}
        onSaved={() => {
          setGstOpen(false);
          load();
        }}
      />
      <AddressDialog
        open={addrDialog.open}
        data={addrDialog.data}
        onClose={() => setAddrDialog({ open: false, data: null })}
        clientId={id}
        onSaved={() => {
          setAddrDialog({ open: false, data: null });
          load();
        }}
      />
      <PersonDialog
        open={personDialog.open}
        data={personDialog.data}
        onClose={() => setPersonDialog({ open: false, data: null })}
        clientId={id}
        onSaved={() => {
          setPersonDialog({ open: false, data: null });
          load();
        }}
      />
      <NoteDialog
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        clientId={id}
        onSaved={() => {
          setNoteOpen(false);
          load();
        }}
      />

      <Box sx={{ mt: 3 }}>
        <CommentsWidget
          entityType="client"
          entityId={id}
          title="Client Comments"
        />
      </Box>
    </Box>
  );
}

// ============ DIALOGS ============

function BasicInfoDialog({ open, onClose, data = {}, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setForm({
      business_name: data.business_name || "",
      contact_person: data.contact_person || "",
      email: data.email || "",
      mobile: data.mobile || "",
      whatsapp: data.whatsapp || "",
      business_type: data.business_type || "",
      address: data.address || "",
      city: data.city || "",
      state: data.state || "",
      pincode: data.pincode || "",
      grade: data.grade || "",
    });
  }, [data, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.post("/clients/update_basic.php", { ...form, id: data.id });
      onSaved();
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Basic Information</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Business Name" value={form.business_name || ""} onChange={(e) => set("business_name", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Contact Person" value={form.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Mobile" value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="WhatsApp" value={form.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Business Type" value={form.business_type || ""} onChange={(e) => set("business_type", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Grade" value={form.grade || ""} onChange={(e) => set("grade", e.target.value)}>
              <MenuItem value="">—</MenuItem>
              {GRADES.map((g) => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="City" value={form.city || ""} onChange={(e) => set("city", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="State" value={form.state || ""} onChange={(e) => set("state", e.target.value)}>
              <MenuItem value="">—</MenuItem>
              {STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Pincode" value={form.pincode || ""} onChange={(e) => set("pincode", e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RegistrationDialog({ open, onClose, data = {}, clientId, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    setForm({
      gst_reg_date: data.gst_reg_date || "",
      gst_state: data.gst_state || "",
      mca_cin: data.mca_cin || "",
      iec_code: data.iec_code || "",
      tan: data.tan || "",
      itr_type: data.itr_type || "",
      client_id: clientId,
    });
  }, [data, clientId, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.post("/client_registration/update.php", form);
      onSaved();
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Registration Details</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="date" label="GST Reg Date" InputLabelProps={{ shrink: true }}
              value={form.gst_reg_date || ""} onChange={(e) => set("gst_reg_date", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="GST State" value={form.gst_state || ""} onChange={(e) => set("gst_state", e.target.value)}>
              <MenuItem value="">—</MenuItem>
              {STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="MCA CIN" value={form.mca_cin || ""} onChange={(e) => set("mca_cin", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="TAN" value={form.tan || ""} onChange={(e) => set("tan", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="ITR Type" value={form.itr_type || ""} onChange={(e) => set("itr_type", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="IEC Code" value={form.iec_code || ""} onChange={(e) => set("iec_code", e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AccountingDialog({ open, onClose, data = {}, clientId, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      accounting_cycle: data.accounting_cycle || "monthly",
      bookkeeper_name: data.bookkeeper_name || "",
      tally_serial: data.tally_serial || "",
      accounting_platform: data.accounting_platform || "",
      client_id: clientId,
    });
  }, [data, clientId, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/client_accounting/update.php", form);
      onSaved();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Accounting Info</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Accounting Cycle" value={form.accounting_cycle || ""} onChange={(e) => set("accounting_cycle", e.target.value)}>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Bookkeeper" value={form.bookkeeper_name || ""} onChange={(e) => set("bookkeeper_name", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Platform" placeholder="Tally, Zoho..." value={form.accounting_platform || ""} onChange={(e) => set("accounting_platform", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Tally Serial" value={form.tally_serial || ""} onChange={(e) => set("tally_serial", e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function GstDialog({ open, onClose, data = {}, clientId, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      gst_username: data.gst_username || "",
      gst_password: data.gst_password || "",
      composition_scheme: data.composition_scheme ? 1 : 0,
      gst_filing_type: data.gst_filing_type || "monthly",
      client_id: clientId,
    });
  }, [data, clientId, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/client_gst/update.php", form);
      onSaved();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit GST Profile</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="GST Username" value={form.gst_username || ""} onChange={(e) => set("gst_username", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth type="password" label="GST Password" value={form.gst_password || ""} onChange={(e) => set("gst_password", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Filing Type" value={form.gst_filing_type || ""} onChange={(e) => set("gst_filing_type", e.target.value)}>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="quarterly">Quarterly</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth label="Composition Scheme" value={form.composition_scheme ?? 0} onChange={(e) => set("composition_scheme", Number(e.target.value))}>
              <MenuItem value={0}>No</MenuItem>
              <MenuItem value={1}>Yes</MenuItem>
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function AddressDialog({ open, onClose, data, clientId, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      type: data?.type || "Registered Office",
      address: data?.address || "",
      city: data?.city || "",
      state: data?.state || "",
      pincode: data?.pincode || "",
      client_id: clientId,
    });
  }, [data, clientId, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const url = data ? "/client_addresses/update.php" : "/client_addresses/create.php";
      await api.post(url, { ...form, id: data?.id });
      onSaved();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data ? "Edit Address" : "Add Address"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField select fullWidth label="Type" value={form.type || ""} onChange={(e) => set("type", e.target.value)}>
              {ADDRESS_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={3} label="Full Address" value={form.address || ""} onChange={(e) => set("address", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="City" value={form.city || ""} onChange={(e) => set("city", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth label="State" value={form.state || ""} onChange={(e) => set("state", e.target.value)}>
              <MenuItem value="">—</MenuItem>
              {STATES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="Pincode" value={form.pincode || ""} onChange={(e) => set("pincode", e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : data ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PersonDialog({ open, onClose, data, clientId, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      name: data?.name || "",
      role: data?.role || "",
      email: data?.email || "",
      mobile: data?.mobile || "",
      client_id: clientId,
    });
  }, [data, clientId, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const url = data ? "/client_key_people/update.php" : "/client_key_people/create.php";
      await api.post(url, { ...form, id: data?.id });
      onSaved();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data ? "Edit Person" : "Add Person"}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Name" value={form.name || ""} onChange={(e) => set("name", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Role" placeholder="Director, Partner..." value={form.role || ""} onChange={(e) => set("role", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Mobile" inputProps={{ maxLength: 10 }} value={form.mobile || ""} onChange={(e) => set("mobile", e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? "Saving…" : data ? "Update" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function NoteDialog({ open, onClose, clientId, onSaved }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  const save = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post("/client_internal_notes/create.php", { client_id: clientId, note });
      onSaved();
    } catch (e) {
      Swal.fire({ icon: "error", title: "Failed", text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Note</DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="Enter your note..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !note.trim()}>
          {saving ? "Saving…" : "Add Note"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
